import _ from 'lodash'
import { useEffect, useMemo, useState } from 'react'

import {
  fetchDistinctFieldValues,
  fetchExistingReferenceListKeys,
  fetchReferenceListOptions,
  getAvailableReferenceListKeys,
  getEntityOwnerFirmId,
  IOptionSource,
  IReferenceListKeyOption,
  IReferenceListOption,
  resolveReferenceListFirmId,
} from 'shared-libs/helpers/reference-list'

import apis from 'browser/app/models/apis'

/**
 * Web binding for the platform-agnostic option-source resolver
 * (shared-libs/helpers/reference-list): the React hooks that supply the browser
 * `apis` instance and manage load state. Pure helpers are imported from shared-libs
 * directly at the call site; only the hooks live here.
 */

export interface IUseAvailableReferenceListKeys {
  options: IReferenceListKeyOption[]
  isLoading: boolean
}

/**
 * The lists this firm can still create — keys declared by fields in its loaded
 * schemas, minus the ones it already owns. Powers the "List Type" picker on the
 * reference list itself; `enabled` is false for every other select so no
 * unrelated field pays for the walk.
 *
 * The schema walk runs in the memo (not per render) to avoid churning identity.
 */
export const useAvailableReferenceListKeys = (
  enabled: boolean,
  firmId?: string
): IUseAvailableReferenceListKeys => {
  const [existingKeys, setExistingKeys] = useState<string[] | undefined>(undefined)

  useEffect(() => {
    if (!enabled) {
      setExistingKeys(undefined)
      return
    }
    let cancelled = false
    setExistingKeys(undefined)
    void fetchExistingReferenceListKeys(apis, firmId).then((keys) => {
      if (!cancelled) {
        setExistingKeys(keys)
      }
    })
    return () => {
      cancelled = true
    }
  }, [enabled, firmId])

  const options = useMemo(() => {
    if (!enabled || existingKeys === undefined) {
      return []
    }
    // The store caches Entity proxies keyed by schema URI — unwrap to raw JSON
    // rather than walking the proxy. See store.cacheRecord / materializeRecord.
    const schemas = _.map(_.values(apis.getStore()?.getSchemas?.() ?? {}), (entity: any) => entity?.content ?? entity)
    return getAvailableReferenceListKeys(schemas, existingKeys)
  }, [enabled, existingKeys])

  return { options, isLoading: enabled && existingKeys === undefined }
}

export interface IUseReferenceListFirmId {
  /** The firm whose reference list to load, per the fallback ladder. */
  firmId: string | undefined
  /** True while an edge-based firm is still resolving — hold the list load until it settles. */
  resolving: boolean
}

/** Reads the edge target id for an `optionSource.referenceListFirmEdge`, if configured/populated. */
const getFirmEdgeTargetId = (entity: any, optionSource?: IOptionSource): string | undefined => {
  const edgePath = optionSource?.referenceListFirmEdge
  if (!edgePath) {
    return undefined
  }
  const edge = entity?.get?.(edgePath) ?? _.get(entity, edgePath)
  return _.get(edge, 'entityId')
}

/**
 * Resolves the firm whose reference list governs a field (see
 * {@link resolveReferenceListFirmId}). No edge → the owner firm, synchronously.
 * With an edge, resolves the target's owner firm async; `resolving` stays true
 * until it settles so the caller doesn't fetch against the wrong firm meanwhile.
 */
export const useReferenceListFirmId = (entity: any, optionSource?: IOptionSource): IUseReferenceListFirmId => {
  const ownerFirmId = getEntityOwnerFirmId(entity)
  const edgeTargetId = getFirmEdgeTargetId(entity, optionSource)
  const hasEdge = !!optionSource?.referenceListFirmEdge && !!edgeTargetId

  const [state, setState] = useState<IUseReferenceListFirmId>(() =>
    hasEdge ? { firmId: undefined, resolving: true } : { firmId: ownerFirmId, resolving: false }
  )

  useEffect(() => {
    if (!hasEdge) {
      setState({ firmId: ownerFirmId, resolving: false })
      return
    }
    let cancelled = false
    setState({ firmId: undefined, resolving: true })
    void resolveReferenceListFirmId(apis, entity, optionSource).then((firmId) => {
      if (!cancelled) {
        setState({ firmId, resolving: false })
      }
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasEdge, edgeTargetId, ownerFirmId])

  return state
}

export interface IUseReferenceListOptions {
  options: IReferenceListOption[]
  isLoading: boolean
  /** true once a load attempt has settled (so callers know when [] means "no list"). */
  loaded: boolean
  /** true when the load failed — the caller shows an error rather than an empty menu. */
  error: boolean
}

/**
 * Hook around {@link fetchReferenceListOptions}. Loads eagerly on mount (and when
 * the key or firm changes) so options are ready before the menu opens. A load
 * failure sets `error` so the field can surface it — an authoritative firm
 * vocabulary silently rendering empty would hide a real problem.
 */
export const useReferenceListOptions = (
  referenceListKey?: string,
  firmId?: string,
): IUseReferenceListOptions => {
  const [options, setOptions] = useState<IReferenceListOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!referenceListKey) {
      setOptions([])
      setIsLoading(false)
      setLoaded(true)
      setError(false)
      return
    }
    setLoaded(false)
    setIsLoading(true)
    setError(false)
    fetchReferenceListOptions(apis, referenceListKey, firmId)
      .then((opts) => {
        if (!cancelled) {
          setOptions(opts)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOptions([])
          setError(true)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
          setLoaded(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [referenceListKey, firmId])

  return { options, isLoading, loaded, error }
}

/**
 * Hook that loads the firm's distinct existing values for a bound field. Pass a
 * falsy `entityType` (or empty `valuePath`) to skip the load — callers use this
 * to only fetch distinct values when `optionSource.distinctValues` is enabled
 * and no reference list supplied options.
 */
export const useDistinctFieldValues = (
  entityType: string | undefined,
  valuePath: string[],
  size?: number,
  firmId?: string,
): IUseReferenceListOptions => {
  const [options, setOptions] = useState<IReferenceListOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const valuePathKey = (valuePath ?? []).join('.')

  useEffect(() => {
    let cancelled = false
    if (!entityType || !valuePathKey) {
      setOptions([])
      setIsLoading(false)
      setLoaded(true)
      return
    }
    setLoaded(false)
    setIsLoading(true)
    fetchDistinctFieldValues(apis, entityType, valuePathKey.split('.'), size, firmId)
      .then((opts) => {
        if (!cancelled) {
          setOptions(opts)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
          setLoaded(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [entityType, valuePathKey, size, firmId])

  // Suggestions are advisory: a failed load degrades to no options, never an error.
  return { options, isLoading, loaded, error: false }
}
